import { ITEM_GAP_PX } from "../utils";

import { buildAtomStream } from "./buildAtomStream";
import { buildRowTree } from "./buildRowTree";
import { selectBreaks } from "./selectBreaks";

import type { InlineNodeContentRenderItem } from "../types";
import type { ActiveContainerFrame, Atom, RenderRow } from "./types";
import type { InlineNodeModel } from "@/models/nodes/InlineNodeModel";

export type CreateWrappedRowsOptions = {
  enableIndentation?: boolean;
  gap?: number;
  /**
   * The inline node that the top-level `items` belong to. Threaded through to
   * each row's items-run so the renderer can wire cursor positions correctly.
   */
  topLevelInline?: InlineNodeModel;
};

export function createWrappedRows(
  items: InlineNodeContentRenderItem[],
  availableWidth: number | undefined,
  widths: Record<string, number>,
  options: CreateWrappedRowsOptions = {},
): RenderRow[] {
  const { enableIndentation = true, gap = ITEM_GAP_PX, topLevelInline } = options;

  const atoms = buildAtomStream(items, { widths });

  const atomRows = selectBreaks(atoms, {
    availableWidth,
    gap,
    enableIndentation,
  });

  const rows: RenderRow[] = [];
  let activeAtStart: ActiveContainerFrame[] = [];

  for (const atomRow of atomRows) {
    const content = buildRowTree(atomRow.atoms, activeAtStart, topLevelInline);
    rows.push({ content, indentation: atomRow.indentation });
    activeAtStart = computeActiveAtEnd(atomRow.atoms, activeAtStart);
  }

  return rows;
}

/**
 * Walk a row's atoms and return the frame stack at the end of it. Tracks
 * `parameterIndex` per container so that a continuation row resumes typing
 * inside the right parameter inline (each `comma` atom on a previous row
 * advances the index here).
 */
function computeActiveAtEnd(
  rowAtoms: Atom[],
  activeAtStart: ActiveContainerFrame[],
): ActiveContainerFrame[] {
  // Clone the frames so we don't mutate the previous row's snapshot.
  const stack: ActiveContainerFrame[] = activeAtStart.map((frame) => ({ ...frame }));

  for (const atom of rowAtoms) {
    if (atom.kind === "open") {
      stack.push({ container: atom.container, parameterIndex: 0 });
    } else if (atom.kind === "close") {
      stack.pop();
    } else if (atom.kind === "comma") {
      const top = stack[stack.length - 1];
      if (top) top.parameterIndex += 1;
    }
  }

  return stack;
}
